import { useEffect, useState } from "react";
import axiosInstance from "../../services/axiosInstance";
import * as S from "./style";
/**
 * Components
 */
import Delete from "../../components/Delete";
import DeleteAll from "../../components/DeleteAll";
import Auth401 from "../../components/Error/Auth";
import Erro429 from "../../components/Error/Erro429";
import Footer from "../../components/Footer";
import Loading from "../../components/Loading";
import Details from "../../components/Modals/OS/Details";
import Update from "../../components/Modals/OS/Update";
import NoData from "../../components/NoData";
import PopMenu from "../../components/PopMenuTable";
import TitleDefault from "../../components/Title/TitleDefault";
import TopActionBar from "../../components/TopActionBar";
import Toast from "../../components/Toast";
/**
 * Hooks
*/
import { useCheckbox } from "../../hooks/useCheckbox";
import useLoading from "../../hooks/useLoading";
import { useMultipleModal } from "../../hooks/useMultipleModal";
/**
 * Interface 
*/
import { IPage } from "Interfaces/IPage";
import { IServiceOrder } from "Interfaces/IServiceOrder";
/**
 * Icons
 */
import { List, PencilSimple, Trash } from "@phosphor-icons/react";
import { FiMoreVertical } from "react-icons/fi";
/**
 * utils
*/
import FormatarData from "utils/FormatData";

export default function Customer() {
  const [data, setData] = useState<IServiceOrder[]>([]);
  const [openModalOptId, setOpenModalOptId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setCurrentPage] = useState<IPage>({
    id: 0,
    last_page: 0,
    current_page: 0,
    total: 0,
    per_page: 0
  });
  const [toastData, setToastData] = useState({
    status: 200,
    message: "",
  });
  const { isOpenModal, toggleModalVisibility, assignRef } = useMultipleModal();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { selectedIds, toggleCheckbox, setSelectedIds, CleanSelectIds } =
    useCheckbox();
  const [selectAll, setSelectAll] = useState(false);


  const listHeaderTable = [
    "#",
    "Nº da O.S",
    "Nº da Venda",
    "Data da Compra",
    "Data da Entrega",
    "Ações"
  ];

  const menuItemsPop = [
    { icon: <List size={16} />, title: "Detalhes", isActive: false, path: "details" },
    { icon: <PencilSimple size={16} />, title: "Editar", isActive: false, path: "update" },
    { icon: <Trash size={16} />, title: "Excluir", isActive: true, path: "delete" },
  ];

  const handleMenuClick = (path: string) => {
    toggleModalVisibility(path);
  };

  const clickPopTable = (id: number) => {
    setOpenModalOptId(id);
    toggleModalVisibility("menu");
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    fetchData();
  };

  //Atualizar dados com registro atual de paginação
  const refreshFrames = () => {
    fetchData(page.current_page, page.per_page);
  };

  const fetchData = async (pageNumber = 1, perPage = 10) => {
    startLoading();
    try {
      let url = `/service-order?page=${pageNumber}&per_page=${perPage}`;
      if (searchTerm.trim()) {
        url += `&search=${searchTerm}`;
      }
      const response = await axiosInstance.get(url);
      setData(response.data.data);
      setCurrentPage(response.data);
    } catch (error: any) {
      if (error.status === 401) {
        toggleModalVisibility('auth');
      } else if (error.status === 429) {
        toggleModalVisibility('request429');
      }
      console.log("Error: ", error);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(debounceFetch);
  }, [searchTerm]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);

    if (isChecked) {
      // Seleciona todos os IDs
      const newSelectedIds = new Set(data.map((item) => item.id));
      setSelectedIds(newSelectedIds);
    } else {
      // Desmarca todos os IDs
      CleanSelectIds();
    }
  };

  const handleCheckChange = (id: number) => {
    toggleCheckbox(id);
  };

  const handleDeleteResponse = (status: number) => {
    if (status === 200) {
      setSelectedIds(new Set());
      setSelectAll(false);
    }
  };

  if (isOpenModal("auth")) {
    return (
      <Auth401 isOpen={isOpenModal("auth")} />
    )
  } else if (isOpenModal("request429")) {
    return (
      <Erro429 />
    )
  }

  return (
    <S.Container>
      <TitleDefault
        filterSearch={handleSearchChange}
        modalKey="service-order"
        title="Registro de Ordem de Serviço"
        subtitle="Gerencie as o.s do sistema"
        placeholderSearch="Nº da ordem de serviço"
        fetchData={fetchData}
        titleButton="Nova O.S"
      />
      {isLoading ? (
        <Loading />
      ) : (
        <S.Content>
          <S.ContaintableButton>
            <S.ContentButton>
              {selectedIds.length > 0 && (
                <TopActionBar
                  idSelecteds={selectedIds}
                  typeProduct="ordem-service"
                  nameDeleteAll="service-order-delete-multiple"
                  fetchData={fetchData}
                  handleDeleteResponse={handleDeleteResponse}
                  isPromo={false}
                />
              )}
            </S.ContentButton>

            <S.Table>
              <S.THead>
                <tr>
                  <th>
                    <S.CheckBox
                      type="checkbox"
                      id="deleteAll"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e)}
                    />
                  </th>
                  {listHeaderTable.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </S.THead>

              <S.TBody>
                {
                  data.length > 0 ? (
                    data.map((val, index) => (
                      <tr key={index}>
                        <td>
                          <S.CheckBox
                            onChange={() => handleCheckChange(val.id)}
                            type="checkbox"
                            id={val.number_os}
                            name={val.number_os}
                            checked={selectedIds.includes(val.id)}
                          />
                        </td>
                        <td>{(page.current_page - 1) * page.per_page + index + 1}</td>
                        <td>{val.number_os || "-"}</td>
                        <td>{val.sale?.number_ata || "-"}</td>
                        <td>{FormatarData(val.sale?.created_at) || "-"}</td>
                        <td>{FormatarData(val.delivery, 'default') || "-"}</td>
                        <S.TdPopMenu>
                          <S.ContentPopMenu>
                            <S.ButtonTable onClick={() => clickPopTable(val.id)}>
                              <FiMoreVertical />
                            </S.ButtonTable>
                          </S.ContentPopMenu>
                          {isOpenModal("menu") && openModalOptId === val.id && (
                            <div ref={(ref) => assignRef("menu", ref)}>
                              <PopMenu menuItems={menuItemsPop} onClickTable={handleMenuClick} />
                            </div>
                          )}
                        </S.TdPopMenu>
                      </tr>
                    ))
                  ) : <NoData />
                }
              </S.TBody>
            </S.Table>
          </S.ContaintableButton>
        </S.Content>
      )}

      {page && (
        <Footer
          fetchData={fetchData}
          currentPage={page.current_page}
          lastPage={page.last_page}
          allPerPage={page.total}
        />
      )}

      <Update
        isOpenModal={isOpenModal("update")}
        setModalClose={() => toggleModalVisibility("update")}
        selectedUrlId={openModalOptId}
        refreshFrames={refreshFrames}
        setToastOpen={() => toggleModalVisibility('open-toast')}
        setToastData={setToastData}
      />

      <Details
        isOpenModalDetails={isOpenModal("details")}
        setModalClose={() => toggleModalVisibility("details")}
        selectedUrlId={openModalOptId}
      />

      <Delete
        updateList={refreshFrames}
        endpoint="service-order"
        selectedProductId={openModalOptId}
        isOpenModal={isOpenModal("delete")}
        setModalClose={() => toggleModalVisibility("delete")}
      />

      <DeleteAll
        setResponse={handleDeleteResponse}
        updateList={refreshFrames}
        data={selectedIds}
        endpoint="service-order-delete-multiple"
        isOpenModal={isOpenModal("deleteAll")}
        setModalClose={() => toggleModalVisibility("deleteAll")}
      />

      <Toast
        openToast={isOpenModal('open-toast')}
        closeToast={() => toggleModalVisibility('open-toast')}
        message={toastData.message}
        status={toastData.status}
      />
    </S.Container>
  );
}
